import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Phone, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["Nuevo", "Contactado", "Cotización enviada", "Cerrado", "Perdido"];
const statusColor = {
  Nuevo: "bg-glg-green/15 text-glg-green",
  Contactado: "bg-glg-blue/15 text-glg-blue",
  "Cotización enviada": "bg-glg-orange/15 text-glg-orange",
  Cerrado: "bg-white/10 text-white/60",
  Perdido: "bg-red-500/15 text-red-300",
};

export default function Leads() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["admin", "leads"],
    queryFn: async () => (await api.get("/admin/leads")).data,
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/leads/${id}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("Estado actualizado");
    },
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/leads/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      toast.success("Lead eliminado");
    },
    onError: () => toast.error("Solo un administrador puede eliminar"),
  });

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold sm:text-3xl">Leads</h1>
      <p className="mt-1 text-sm text-white/50">Formularios de contacto recibidos</p>

      {isLoading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>
      ) : leads.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-white/15 py-16 text-center text-white/40">Aún no hay leads.</div>
      ) : (
        <div className="mt-8 space-y-3">
          {leads.map((l) => (
            <div key={l.id} data-testid="lead-item" className="rounded-2xl border border-white/10 bg-glg-surface/60 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-white">{l.name}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
                    {l.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span>}
                    {l.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{l.company}</span>}
                    {l.service && <span>Interés: {l.service}</span>}
                  </div>
                  {l.message && <p className="mt-2 max-w-2xl text-sm text-white/70">{l.message}</p>}
                  <div className="mt-2 text-xs text-white/30">{new Date(l.created_at).toLocaleString("es-HN")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-xs ${statusColor[l.status] || ""}`}>{l.status}</span>
                  <select
                    value={l.status}
                    onChange={(e) => statusMut.mutate({ id: l.id, status: e.target.value })}
                    data-testid="lead-status-select"
                    className="rounded-lg border border-white/10 bg-glg-black/60 px-2 py-1.5 text-xs text-white outline-none"
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-glg-surface">{s}</option>)}
                  </select>
                  {user?.role === "admin" && (
                    <button onClick={() => { if (window.confirm("¿Eliminar lead?")) delMut.mutate(l.id); }} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 text-red-300/70 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
